/**
 * Dashboard — rollenabhängige Startseite nach dem Login (Design-Canvas
 * "ShiftSync Rollenansichten": Main.dc.html = Chef, Filialleitung.dc.html =
 * Filialleitung, Kapitel 15).
 *
 * Wird ausschließlich für Chef/Owner und Filialleitung gerendert — reine
 * Mitarbeiter landen weiterhin direkt auf ShiftsPage (siehe StartPage in
 * App.tsx, die anhand der Rolle entscheidet, BEVOR diese Komponente
 * überhaupt gemountet wird).
 *
 * Datenquellen bewusst getrennt: /api/dashboard liefert die aggregierten
 * Kennzahlen (Standorte, Stunden, Team, Einsprünge — bereits Lohn-/
 * Zusatzstunden-maskiert wie überall sonst), die BEDIENBARE
 * Urlaubs-/Krank-Antragsliste kommt wie in TeamOverviewPage separat über
 * calendarEntriesApi (braucht `lastModified` für den Genehmigen/Ablehnen-
 * Request) — keine doppelte Fach-Logik für dieselbe Sache an zwei Stellen.
 *
 * Visuelle Angleichung ans Design-Canvas-Artefakt "ShiftSync Rollenansichten"
 * (Main.dc.html / Filialleitung.dc.html): Icons, Avatare und Wortlaut sind
 * 1:1 aus den Mockup-SVGs übernommen statt reinem Text — die App hat keine
 * Icon-Bibliothek, daher handgerollte Inline-SVGs statt einer neuen
 * Abhängigkeit. Icon-Set + Avatar liegen in components/icons.tsx und
 * components/Avatar.tsx (Nachtrag Standort-Detail-Ansicht, 08.09.2026 —
 * dieselben Bausteine werden jetzt auch dort gebraucht).
 */

import { Link } from 'react-router-dom';
import { useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, useCanManage, useManagedLocationIds } from '../store/auth';
import { dashboardApi, type DashboardManagerLocation } from '../lib/dashboard';
import { calendarEntriesApi, canDecideEntry, ENTRY_TYPE_META, needsApproval, type ApiCalendarEntry } from '../lib/calendarEntries';
import { usersApi } from '../lib/users';
import { formatDate, formatMoney } from '../lib/format';
import { hapticError, hapticSuccess } from '../lib/haptics';
import { Avatar } from '../components/Avatar';
import {
  IconChevronRight,
  IconChevronDown,
  IconPlus,
  IconPeople,
  IconClock,
  IconLock,
  IconUnlock,
  IconCheck,
  IconCard,
  IconUser,
  IconCalendar,
} from '../components/icons';

const fmtH = (h: number) => `${h.toFixed(1).replace('.', ',')} Std.`;

function vacationLabel(n: number): string {
  return n === 1 ? '1 Urlaubsantrag' : `${n} Urlaubsanträge`;
}

export default function DashboardPage() {
  const token = useAuth((s) => s.token)!;
  const activeCompanyId = useAuth((s) => s.activeCompanyId);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', activeCompanyId],
    enabled: !!activeCompanyId,
    queryFn: () => dashboardApi.get(token),
  });

  const { data: pendingEntries } = useQuery({
    queryKey: ['calendar-entries', 'pending', activeCompanyId],
    enabled: !!activeCompanyId,
    queryFn: () => calendarEntriesApi.list(token, { status: 'pending' }),
  });
  const { data: users } = useQuery({ queryKey: ['users', activeCompanyId], queryFn: () => usersApi.list(token) });
  const me = useAuth((s) => s.user);
  const isOwner = useCanManage();
  const managedLocationIds = useManagedLocationIds();
  // Gesamtprüfung 11.09.2026: nur Anträge zeigen, über die man auch
  // entscheiden darf (Server lehnt die eigenen bzw. fremde Haupt-Standorte ab).
  const pendingAbsences = useMemo(
    () =>
      (pendingEntries ?? []).filter(
        (e) =>
          needsApproval(e.type) &&
          !e.deletedAt &&
          canDecideEntry(e, {
            meId: me?.id ?? '',
            isOwner,
            managedLocationIds,
            activeCompanyId,
            users: users ?? [],
          }),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingEntries, users, me?.id, isOwner, managedLocationIds.join(','), activeCompanyId],
  );
  const nameById = useMemo(() => new Map((users ?? []).map((u) => [u.id, u.name])), [users]);

  async function decide(entry: ApiCalendarEntry, status: 'approved' | 'rejected') {
    try {
      await calendarEntriesApi.update(token, entry.id, { status }, entry.lastModified);
      hapticSuccess();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['calendar-entries', 'pending'] }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    } catch (err) {
      hapticError();
      alert(err instanceof Error ? err.message : 'Entscheidung fehlgeschlagen');
    }
  }

  if (!activeCompanyId) {
    return (
      <div className="max-w-md mx-auto mt-10 text-center text-slate-400">
        <p>Bitte oben eine Firma auswählen, um das Dashboard zu sehen.</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return <div className="max-w-md mx-auto mt-10 text-center text-slate-400">Lädt…</div>;
  }

  if (data.role === 'none') {
    // Sicherheitsnetz — sollte praktisch nie gerendert werden (StartPage
    // schickt Mitarbeiter ohne Verwaltungsrechte direkt auf ShiftsPage).
    return (
      <div className="max-w-md mx-auto mt-10 text-center text-slate-400">
        <p>Kein Dashboard für diese Ansicht verfügbar.</p>
      </div>
    );
  }

  const absencesSection = (
    <Section title={`Urlaubsanträge${pendingAbsences.length > 0 ? ` (${pendingAbsences.length})` : ''}`}>
      {pendingAbsences.length === 0 ? (
        <p className="text-sm text-slate-500 px-1">Keine offenen Anträge.</p>
      ) : (
        <div className="card divide-y divide-border/60">
          {pendingAbsences.map((e) => {
            const meta = ENTRY_TYPE_META[e.type];
            return (
              <div key={e.id} className="p-3 flex items-center gap-3">
                <Avatar name={nameById.get(e.userId) ?? '?'} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {nameById.get(e.userId) ?? '?'} · <span style={{ color: meta.color }}>{meta.label}</span>
                    {e.halfDay && <span className="text-xs text-slate-400"> · halber Tag</span>}
                  </p>
                  <p className="text-xs text-slate-400">{formatDate(e.date)}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    onClick={() => void decide(e, 'approved')}
                    className="text-xs bg-emerald-600/80 hover:bg-emerald-600 rounded px-2.5 py-1.5 !min-h-0"
                  >
                    Genehmigen
                  </button>
                  <button
                    onClick={() => void decide(e, 'rejected')}
                    className="text-xs bg-red-600/80 hover:bg-red-600 rounded px-2.5 py-1.5 !min-h-0"
                  >
                    Ablehnen
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );

  const meineAnsichtSection = (
    <Section title="Für dich">
      <NavCard
        to="/meine-ansicht"
        icon={<IconUser width={18} height={18} />}
        title="Meine Ansicht"
        desc="Eigene Liste, Kalender & Übersicht — für Schichten, die du selbst übernimmst."
      />
    </Section>
  );

  if (data.role === 'owner') {
    return (
      <div className="max-w-md lg:max-w-2xl mx-auto space-y-6 pb-12">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Standorte</h1>
          <Link to="/admin/locations?neu=1" className="btn text-xs px-3 py-1.5 !min-h-0">
            <IconPlus width={15} height={15} strokeWidth={2.2} />
            Filiale
          </Link>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <Kpi label="Standorte" value={String(data.stats.locationsCount)} />
          <Kpi label="Mitarbeiter" value={String(data.stats.employeesCount)} />
          <Kpi label="Urlaubsanträge" value={String(data.stats.openVacationRequests)} highlight />
        </div>

        <div className="space-y-2.5">
          {data.locations.map((l) => (
            <Link
              key={l.id}
              to={`/admin/locations/${l.id}`}
              className="card p-3.5 flex flex-col gap-2 hover:bg-surface-2 transition !min-h-0"
              style={{ borderLeft: `4px solid ${l.color}` }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-[15px] truncate">{l.name}</span>
                  {l.branchNumber && (
                    <span className="badge bg-slate-700 text-slate-300 font-mono shrink-0">Nr. {l.branchNumber}</span>
                  )}
                  {!l.isActive && <span className="badge bg-slate-700 text-slate-400 shrink-0">inaktiv</span>}
                </div>
                <IconChevronRight width={18} height={18} className="text-slate-500 shrink-0" />
              </div>
              {l.address && <p className="text-[12.5px] text-slate-400 truncate">{l.address}</p>}
              <div className="flex items-center gap-3.5 text-[12.5px] text-slate-400 flex-wrap">
                <span className="inline-flex items-center gap-1.5">
                  <IconPeople width={14} height={14} className="text-slate-400" />
                  {l.employeeCount} Mitarbeiter
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <IconClock width={14} height={14} className="text-slate-400" />
                  {fmtH(l.hoursThisWeek)} diese Woche
                </span>
                {l.openVacationRequests > 0 && (
                  <span className="badge bg-amber-500/20 text-amber-300">{vacationLabel(l.openVacationRequests)}</span>
                )}
              </div>
            </Link>
          ))}
          {data.locations.length === 0 && (
            <p className="text-sm text-slate-500 px-1">Noch keine Standorte angelegt.</p>
          )}
        </div>

        <Section title="Firma">
          <NavCard
            to="/team"
            icon={<IconLock width={18} height={18} />}
            title="Lohnrechnung"
            desc="Alle Standorte, nur für dich sichtbar."
          />
          <NavCard
            to="/team"
            icon={<IconCard width={18} height={18} />}
            title="Vorschuss-Anfragen"
            desc="Bestätigen oder ablehnen."
            badge={data.pendingAdvancesCount > 0 ? String(data.pendingAdvancesCount) : undefined}
          />
        </Section>

        {meineAnsichtSection}
      </div>
    );
  }

  // --- Filialleitung / Bereichsleiter ---
  const isBereichsleiter = data.isBereichsleiter;

  return (
    <div className="max-w-md lg:max-w-2xl mx-auto space-y-6 pb-12">
      <div>
        <div className="card p-3 flex items-center gap-2">
          <span className="badge bg-blue-500/20 text-blue-300 shrink-0">
            {isBereichsleiter ? 'Bereichsleitung' : 'Filialleitung'}
          </span>
          <span className="text-[14.5px] font-semibold truncate flex-1">
            {data.locations.map((l) => l.name).join(' + ') || 'Kein Standort'}
          </span>
          <IconChevronDown width={14} height={14} className="text-slate-400 shrink-0" />
        </div>
        {isBereichsleiter && (
          <p className="text-[11.5px] text-slate-500 mt-1 px-0.5">
            Du leitest {data.locations.length} Standorte · {data.locations.map((l) => l.name).join(' + ')}
          </p>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Kpi label="Mitarbeiter" value={String(data.stats.employeeCount)} />
        <Kpi label="Std. (Woche)" value={fmtH(data.stats.hoursThisWeek)} />
        <Kpi label="Urlaubsanträge" value={String(pendingAbsences.length)} highlight />
      </div>

      <Section title="Planung">
        <NavCard
          to="/wochenplan"
          icon={<IconCalendar width={18} height={18} />}
          title="Wochenplan erstellen"
          desc="Schichten fürs Team planen und veröffentlichen."
        />
      </Section>

      {data.locations.map((l) => (
        <LocationTeamSection key={l.id} location={l} />
      ))}

      {absencesSection}
      {meineAnsichtSection}
    </div>
  );
}

/**
 * Nachtrag 08.09.2026 (Shadow: "schwierig ein[en] Unterschied zu sehen
 * zwischen den Filialen") — bei mehreren gestapelten Standorten
 * (Bereichsleitung) sah bisher jeder Block identisch aus (nur der
 * Text-Header unterschied sich). Jetzt: farbiger Rahmen + Karten-Hintergrund
 * pro Standort (dieselbe Farbe, die der Chef auch sonst für diesen Standort
 * vergibt, z. B. Standort-Kacheln im Chef-Dashboard) + Filialnummer-Badge,
 * damit jeder Block optisch klar als eigener Standort erkennbar ist.
 */
function LocationTeamSection({ location: l }: { location: DashboardManagerLocation }) {
  return (
    <div
      className="rounded-xl border bg-surface-2/30 p-3 space-y-2.5"
      style={{ borderColor: `${l.color}55`, borderLeftWidth: 4, borderLeftColor: l.color }}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Link
          to={`/admin/locations/${l.id}`}
          className="label text-xs uppercase tracking-wider text-slate-300 hover:text-white !min-h-0 inline-flex items-center gap-1.5"
        >
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: l.color }} aria-hidden />
          {l.name}
          {l.branchNumber && (
            <span className="badge bg-slate-700 text-slate-400 font-mono normal-case tracking-normal">
              Nr. {l.branchNumber}
            </span>
          )}
          <IconChevronRight width={12} height={12} className="text-slate-500" />
        </Link>
        {l.wageVisible ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
            <IconUnlock width={12} height={12} />
            € freigegeben
          </span>
        ) : (
          <span className="text-[11px] text-slate-500">€ nicht freigegeben</span>
        )}
      </div>
      <div className="card divide-y divide-border/60">
        {l.team.length === 0 ? (
          <p className="text-sm text-slate-500 p-3">Keine Schichten diese Woche.</p>
        ) : (
          l.team.map((row) => (
            <div key={row.userId} className="p-3 flex items-center gap-3">
              <Avatar name={row.name} />
              <span className="text-[13.5px] truncate flex-1">{row.name}</span>
              <span className="text-[13px] text-slate-400 text-right shrink-0">
                {fmtH(row.hours)}
                {/* Nachtrag 11.09.2026: row.wage ist null, wenn nicht freigegeben —
                    die eigene Zeile liefert das Backend immer mit Lohn. */}
                {row.wage !== null && (
                  <>
                    <br />
                    <span className="font-mono text-slate-200">{formatMoney(row.wage)}</span>
                  </>
                )}
              </span>
            </div>
          ))
        )}
      </div>

      {l.einspringen.length > 0 && (
        <>
          <div className="flex items-center justify-between pt-1">
            {/* Nachtrag 11.09.2026 (Shadow): Abschnitt zeigt ALLE selbst
                eingetragenen Schichten — echte Einsprünge (fremder Standort)
                bekommen unten ein eigenes Badge. */}
            <div className="label text-xs uppercase tracking-wider text-slate-400">Selbst eingetragen — {l.name}</div>
            {l.bonusVisible && (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
                <IconCheck width={12} height={12} />
                Zusatzstunden sichtbar
              </span>
            )}
          </div>
          <div className="space-y-2">
            {l.einspringen.map((e, i) => (
              <div key={`${e.userId}-${e.date}-${i}`} className={`card p-3 flex flex-col gap-1.5 border-l-[3px] ${e.isForeign ? 'border-amber-400/70' : 'border-slate-600/60'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar name={e.name} />
                    <p className="text-[13.5px] font-medium truncate">
                      {e.name}
                      {e.homeLocationName && (
                        <span className="text-slate-400 font-normal"> (sonst {e.homeLocationName})</span>
                      )}
                    </p>
                    {e.isForeign && (
                      <span className="badge bg-amber-500/20 text-amber-300 shrink-0">Einsprung</span>
                    )}
                  </div>
                  {e.bonusStatus === 'pending' && (
                    <span className="badge bg-amber-500/20 text-amber-300 shrink-0">wartet auf Chef</span>
                  )}
                </div>
                <p className="text-xs text-slate-400">
                  {formatDate(e.date)} · {e.startTime}–{e.endTime}
                  {e.bonusHours !== null && e.bonusHours > 0 && (
                    <> · davon {e.bonusHours.toFixed(1).replace('.', ',')} Std. Zusatzstunden</>
                  )}
                </p>
                {/* bonusStatus null = für mich nicht freigegeben (eigene Einträge sieht man immer) */}
                {e.bonusStatus === null && e.bonusPending && (
                  <p className="text-[11px] text-slate-500">
                    Nur der Chef entscheidet über Zusatzstunden — dir fehlt hier die Freigabe zum Einsehen.
                  </p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs uppercase tracking-wider text-slate-400 px-1">{title}</h3>
      {children}
    </section>
  );
}

function NavCard({
  to,
  icon,
  title,
  desc,
  badge,
}: {
  to: string;
  icon: ReactNode;
  title: string;
  desc: string;
  badge?: string;
}) {
  return (
    <Link to={to} className="card p-3.5 flex items-center gap-3 hover:bg-surface-2 transition !min-h-0">
      <span className="text-slate-400 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-slate-400 truncate">{desc}</p>
      </div>
      {badge && <span className="badge bg-amber-500/20 text-amber-300 shrink-0">{badge}</span>}
      <IconChevronRight width={16} height={16} className="text-slate-500 shrink-0" />
    </Link>
  );
}

function Kpi({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="card p-2 sm:p-3 min-w-0">
      <p className="text-[11px] sm:text-xs text-slate-400 truncate">{label}</p>
      <p className={`text-base sm:text-lg font-semibold mt-0.5 truncate ${highlight ? 'text-amber-300' : ''}`}>
        {value}
      </p>
    </div>
  );
}
